import { useEffect, useState } from 'react';
import { useAppState } from '../context/LoadTestContext';

// Units for the Load Testing dashboard.
//
// The unit system is the user's account setting (see services/unitPreference.js);
// GPS Licenses and Calibrations follow the same one. The dashboard's own toggle
// writes it too, so switching here switches everywhere and is saved to the account.
//
// Source units, as the backend and the page's own readings produce them:
// deflection/settlement in inches, load in kips, gauge depth entered by the
// user (kept canonically in metres), pressure in kPa.

import { UNITS_CHANGE_EVENT as CHANGE_EVENT, readUnitSystem, writeUnitSystem } from '../services/unitPreference';

export { readUnitSystem, writeUnitSystem };

export const IN_TO_MM = 25.4;
export const KIPS_TO_KN = 4.4482216152605;
export const M_TO_FT = 1 / 0.3048;
export const KPA_TO_PSI = 0.1450377377;

export function useUnitSystem() {
  const [system, setSystem] = useState(readUnitSystem);
  useEffect(() => {
    const onChange = () => setSystem(readUnitSystem());
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onChange); // another tab flipped it
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  return system;
}

const toNumber = (v) => {
  if (v === null || v === undefined || v === '') return NaN;
  return typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
};

function formatter(convert, decimals, { grouping = false } = {}) {
  return (v) => {
    const n = toNumber(v);
    if (!Number.isFinite(n)) return '—';
    const out = convert(n);
    if (!Number.isFinite(out)) return '—';
    return grouping
      ? out.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
      : out.toFixed(decimals);
  };
}

/**
 * Converters and labels for one unit system. Each `to*` takes a value in the
 * source unit and returns a number in the display unit; each `fmt*` returns a
 * display string ('—' for missing values).
 */
export function unitsFor(system) {
  const metric = system === 'metric';

  const toLength = (inches) => (metric ? inches * IN_TO_MM : inches);
  const lengthDecimals = metric ? 2 : 3;
  const toForce = (kips) => (metric ? kips * KIPS_TO_KN : kips);
  const fromForce = (value) => (metric ? value / KIPS_TO_KN : value);
  const toDepth = (metres) => (metric ? metres : metres * M_TO_FT);
  const fromDepth = (value) => (metric ? value : value / M_TO_FT);
  const toPressure = (kpa) => (metric ? kpa : kpa * KPA_TO_PSI);

  return {
    system,
    metric,

    lengthUnit: metric ? 'mm' : 'in',
    toLength,
    lengthDecimals,
    fmtLength: formatter(toLength, lengthDecimals),
    // Settlement per minute; source in inches/min.
    rateUnit: metric ? 'mm/min' : 'in/min',
    fmtRate: formatter(toLength, metric ? 3 : 4),

    forceUnit: metric ? 'kN' : 'kips',
    toForce,
    fromForce,
    fmtForce: formatter(toForce, 1, { grouping: true }),

    depthUnit: metric ? 'm' : 'ft',
    toDepth,
    fromDepth,

    pressureUnit: metric ? 'kPa' : 'psi',
    fmtPressure: formatter(toPressure, metric ? 0 : 1, { grouping: true }),

    // Axis spans that read well in each system (the deflection graph's floor,
    // and the smallest zoom windows).
    minDeflectionSpan: metric ? 5 : 0.25,
    minZoomDeflectionSpan: metric ? 0.5 : 0.02,
    minZoomLoadSpan: metric ? 5 : 1,
  };
}

export function useUnits() {
  const units = unitsFor(useUnitSystem());
  const state = useAppState();
  const measurementUnits = state?.measurementUnits ?? {};

  function forSensor(type, channel) {
    const unitDefinition = measurementUnits?.[type];
    const sourceUnit = typeof unitDefinition === 'string' ? unitDefinition : channel != null ? unitDefinition?.[channel] : undefined;
    // const sourceUnit = type === 'loadcell' ? measurementUnits?.loadcell : measurementUnits[type]?.[channel];

    let unit = sourceUnit ?? 'unknown';
    let convert = (value) => value;
    let decimals = 3;

    switch (sourceUnit) {
      case 'kips':
        unit = units.forceUnit;
        convert = units.toForce;
        decimals = 1;
        break;

      case 'in':
        unit = units.lengthUnit;
        convert = units.toLength;
        decimals = units.lengthDecimals;
        break;

      case 'microstrain':
        unit = 'με';
        decimals = 1;
        break;

      case 'kPa':
        unit = units.pressureUnit;
        convert = (value) => (units.metric ? value : value * KPA_TO_PSI);
        decimals = units.metric ? 0 : 1;
        break;
      case 'digits':
      case 'mV':
        break;
      default:
        convert = () => NaN;
        break;
    }

    return {
      sourceUnit,
      unit,
      toDisplay: (value) => convert(toNumber(value)),
      format: formatter(convert, decimals, { grouping: true }),
    };
  }

  const loadcell = forSensor('loadcell');

  return {
    ...units,
    forSensor,
    forceUnit: loadcell.unit,
    toForce: loadcell.toDisplay,
    fmtForce: loadcell.format,
  }

}

// Gauge depths are typed in the current unit but kept in metres, so flipping
// units back and forth never drifts. Returns the depth string to show for a
// canonical value.
export function depthInputValue(metres, units) {
  if (metres === null || metres === undefined || !Number.isFinite(metres)) return '';
  return String(Number(units.toDepth(metres).toFixed(2)));
}

export function parseDepthInput(text, units) {
  const n = toNumber(text);
  return Number.isFinite(n) ? units.fromDepth(n) : null;
}

// The same for loads typed into the dashboard (design, test and max load):
// entered in the current unit, kept in kips.
export function forceInputValue(kips, units) {
  if (kips === null || kips === undefined || !Number.isFinite(kips)) return '';
  return String(Number(units.toForce(kips).toFixed(1)));
}

export function parseForceInput(text, units) {
  const n = toNumber(text);
  return Number.isFinite(n) ? units.fromForce(n) : null;
}
